import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GridData from 'src/components/GridData/GridData';
import { TableHeader, TableHeaderItem } from 'src/components/TableHeader/TableHeader';
import { Button, Dropdown } from 'src/UIKit';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { getDateTimeUSAFormat, getTimePassed } from 'src/constants/helpers';
import { handleDownload } from 'src/helpers/helpers';
import { useCampaignsApi } from 'src/modules/campaigns/api/CampaignsApi';
import { SurveyExportFormat, SurveyListItem } from 'src/modules/campaigns/types';
import { useSurveysViewController } from '../../controllers';
import { CampaignStatus } from 'src/modules/campaigns/types/CampaignStatus';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { getCampaignConfigs } from 'src/modules/campaignConfigBuilder/slices';
import { useCampaignConfigLoader } from 'src/modules/campaignConfigBuilder/hooks/useCampaignConfigLoader';
import { Breadcrumb } from 'src/components/Breadcrumb';
import { SurveyTimePeriodFilter } from '../SurveyTimePeriodFilter';
import { SurveyTimePeriodFilterValue } from 'src/modules/surveyResponses/types/SurveyTimePeriod';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';
import { campaignBuilderPath } from 'src/modules/campaigns/helpers';

export const SurveysView = () => {
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentSurveyRow, setCurrentSurveyRow] = useState<SurveyListItem | null>(null);
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const campaignConfigs = useSelector(getCampaignConfigs);
  const { loadCampaignConfigs } = useCampaignConfigLoader();
  const navigate = useNavigate();

  const {
    isPageLoading,
    surveysFilter,
    surveysList,
    changeSurveyFilterForm,
    changeSurveysSearchFilter,
    changeSurveysSorting,
    changeSurveysPagination,
  } = useSurveysViewController();

  const { getSurveyExport } = useCampaignsApi();
  const surveyConfig = useMemo(
    () => campaignConfigs.find(config => config.config?.name === 'Survey'),
    [campaignConfigs],
  );

  const handleCreateSurvey = () => {
    if (!surveyConfig?.id) {
      console.error('Survey campaign config not found.');
      return;
    }
    navigate(campaignBuilderPath(`?configId=${surveyConfig.id}`));
  };

  useEffect(() => {
    if (campaignConfigs.length === 0) {
      loadCampaignConfigs();
    }
  }, [campaignConfigs.length, loadCampaignConfigs]);

  const handleDownloadExport = async (surveyId: string, format: SurveyExportFormat) => {
    try {
      const response = await getSurveyExport(surveyId, currentCompanyId, format, {
        startDateInterval: surveysFilter.startDateInterval,
        endDateInterval: surveysFilter.endDateInterval,
      });
      handleDownload(response);
    } catch (error) {
      reportErrorToUser(error, GENERIC_ERROR_MESSAGE);
    }
    setDownloadMenuAnchor(null);
  };

  const handleTimePeriodChange = (value: SurveyTimePeriodFilterValue) => {
    changeSurveyFilterForm({
      ...value,
      page: 0,
      offset: 0,
    });
  };

  return (
    <>
      <PrivateLayoutView
        title={
          <Breadcrumb items={[{ label: 'Surveys', isActive: true }]} />
        }
      >
        <GridData
          header={
            <TableHeader
              searchValue={surveysFilter.query}
              onSearchChange={changeSurveysSearchFilter}
              filters={
                <>
                  <TableHeaderItem>
                    <Dropdown
                      label="Status"
                      value={surveysFilter.status}
                      options={[
                        { title: 'All', value: 'active,inactive' },
                        { title: 'Active', value: 'active' },
                        { title: 'Inactive', value: 'inactive' },
                      ]}
                      onChange={(_, v) => changeSurveyFilterForm({ status: v })}
                      margin="none"
                    />
                  </TableHeaderItem>

                  <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                    <SurveyTimePeriodFilter
                      value={{
                        timePeriod: surveysFilter.timePeriod,
                        startDateInterval: surveysFilter.startDateInterval,
                        endDateInterval: surveysFilter.endDateInterval,
                      }}
                      onChange={handleTimePeriodChange}
                    />
                  </Box>
                </>
              }
              actions={
                <Button
                  variant="contained"
                  onClick={handleCreateSurvey}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Create Survey Campaign
                </Button>
              }
            />
          }
          isLoading={isPageLoading}
          rows={surveysList?.items}
          noRowsOverlay={() => (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
                padding: 3,
              }}
            >
              <Typography variant="body2" align="center">
                You currently don&apos;t have any survey results. Create your first survey campaign to get
                started!
              </Typography>
              <Button variant="contained" onClick={handleCreateSurvey}>
                Create Survey Campaign
              </Button>
            </Box>
          )}
          columns={[
            {
              field: 'name',
              headerName: 'Survey Name',
              renderCell: r => {
                return (
                  <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
                    <Typography variant="body2" noWrap title={r.row.name}>{r.row.name}</Typography>
                    {r.row.status === ('draft' as CampaignStatus) && (
                      <Box sx={{ flexShrink: 0 }}>
                        <Chip color="default" label="Draft" size="small" variant="outlined" />
                      </Box>
                    )}
                  </Stack>
                );
              },
              display: 'flex',
              minWidth: 240,
              editable: false,
              sortable: true,
              flex: 2,
            },
            {
              field: 'createdAt',
              headerName: 'Created At',
              renderCell: r => {
                return getDateTimeUSAFormat(new Date(r.row.createdAt));
              },
              minWidth: 130,
              editable: false,
              sortable: true,
              flex: 1,
              initVisibility: false,
            },
            {
              field: 'updatedAt',
              headerName: 'Updated At',
              renderCell: r => {
                return getDateTimeUSAFormat(new Date(r.row.updatedAt));
              },
              minWidth: 130,
              editable: false,
              sortable: true,
              flex: 1,
            },
            {
              field: 'status',
              headerName: 'Status',
              renderCell: r => {
                return (
                  <Chip
                    label={r.row.status}
                    color={r.row.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                );
              },
              minWidth: 90,
              editable: false,
              sortable: true,
              flex: 1,
              initVisibility: false,
            },
            {
              field: 'numberOfQuestions',
              headerName: 'Questions',
              renderCell: r => {
                return r.row.numberOfQuestions;
              },
              minWidth: 90,
              editable: false,
              sortable: true,
              flex: 1,
            },
            {
              field: 'numberOfContacts',
              headerName: 'Sent to Contacts',
              renderCell: r => {
                return r.row.numberOfContacts;
              },
              minWidth: 110,
              editable: false,
              sortable: true,
              flex: 1,
            },
            {
              field: 'numberOfRespondedContacts',
              headerName: 'Contacts Responded',
              renderCell: r => {
                return r.row.numberOfRespondedContacts;
              },
              minWidth: 130,
              editable: false,
              sortable: true,
              flex: 1,
            },
            {
              field: 'totalReplies',
              headerName: 'Total Replies',
              renderCell: r => {
                return r.row.totalReplies;
              },
              minWidth: 100,
              editable: false,
              sortable: true,
              flex: 1,
            },
            {
              field: 'lastResponseAt',
              headerName: 'Last Response At',
              renderCell: r => {
                if (!r.row.lastResponseAt) {
                  return 'N/A';
                }

                return getTimePassed(r.row.lastResponseAt) + ' ago';
              },
              minWidth: 130,
              editable: false,
              sortable: true,
              flex: 1,
            },
          ]}
          renderAction={r => {
            return (
              <>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="View survey responses"
                  onClick={() => {
                    navigate(`/survey-responses/${r.id}`);
                  }}
                >
                  <VisibilityIcon fontSize="inherit" />
                </IconButton>

                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Export survey responses"
                  onClick={(event) => {
                    setDownloadMenuAnchor(event.currentTarget);
                    setCurrentSurveyRow(r);
                  }}
                >
                  <DownloadIcon fontSize="inherit" />
                </IconButton>
              </>
            );
          }}
          sorting={{
            value: surveysFilter.sort,
            onChange: changeSurveysSorting,
          }}
          pagination={{
            page: surveysFilter.page,
            pageSize: surveysFilter.limit,
            totalCount: surveysList?.count,
            onChange: changeSurveysPagination,
          }}
        />

        <Menu
          anchorEl={downloadMenuAnchor}
          open={Boolean(downloadMenuAnchor)}
          onClose={() => {
            setDownloadMenuAnchor(null);
            setCurrentSurveyRow(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => {
            if (currentSurveyRow) {
              handleDownloadExport(currentSurveyRow.id, 'csv');
            }
          }}>
            Download Responses as CSV
          </MenuItem>
          <MenuItem onClick={() => {
            if (currentSurveyRow) {
              handleDownloadExport(currentSurveyRow.id, 'xlsx');
            }
          }}>
            Download Responses as XLSX
          </MenuItem>
        </Menu>
      </PrivateLayoutView>
    </>
  );
};
